import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from 'react-router-dom';
import Network from '../../../models/Network.js';

import * as vega from 'vega';
import * as vegaLite from 'vega-lite';
import * as vl from 'vega-lite-api';
import { Handler } from 'vega-tooltip';

const key = process.env.REACT_APP_CLIENT_ACCESS_KEY;
const URLS = require("../../../urls.js");

const StatusCodes = require('../../../status-codes/status-codes.js')

const width = 700;
const height = 400;

const config = {
    axis: {
        domain: false,
        tickColor: 'lightGray',
    },
    style: {
        'guide-label': {
            fontSize: 12,
            fill: '#000'
        },
        'guide-title': {
            fontSize: 18,
            fill: '#000'
        }
    },
    legend: {
        disable: true
    }
};

// registration
vl.register(vega, vegaLite, {
    view: { renderer: 'svg' },
    init: view => { view.tooltip(new Handler().call); }
});

const GraphApiTimesReports = ({ data, graphParams }) => {
    const navigate = useNavigate();
    const containerRef = useRef(null);
    const [vegaSpec, setVegaSpec] = useState(null);
    const [selectedTimeMetric, setSelectedTimeMetric] = useState('response');

    // Use data directly without processing
    const processedData = useMemo(() => {
        return data || [];
    }, [data]);

    // Time metric options
    const timeMetrics = [
        { value: 'response', label: 'Response Time', field: 'reaction_time + driving_to_scene_time' },
        { value: 'on_scene', label: 'On-Scene Time', field: 'total_on_scene_time' },
        { value: 'wall', label: 'Wall Time', field: 'wall_time' },
        { value: 'recovery', label: 'Recovery Time', field: 'recovery_time' },
        { value: 'reporting', label: 'Reporting Time', field: 'reporting_time' }
    ];

    // Get the selected metric configuration
    const selectedMetric = timeMetrics.find(m => m.value === selectedTimeMetric) || timeMetrics[0];

    // Calculate filtered times and 0.9 quantile for selected metric
    const filteredTimes = useMemo(() => {
        if (!processedData.length) return { times: [], quantile90: 0 };
        
        const times = processedData
            .map(d => {
                if (selectedMetric.value === 'response') {
                    return (d.reaction_time + d.driving_to_scene_time) / 60000;
                } else {
                    return d[selectedMetric.field.replace('_time', '_time')] / 60000;
                }
            })
            .filter(time => {
                // Don't apply filter for reporting time
                if (selectedMetric.value === 'reporting') {
                    return time >= 0; // Include all reporting times including zeros
                }
                return time > 0 && time <= 200; // Apply filter for other metrics
            });
        
        // Calculate 0.9 quantile (90th percentile)
        const sortedTimes = times.sort((a, b) => a - b);
        const quantileIndex = Math.floor(sortedTimes.length * 0.9);
        const quantile90 = sortedTimes[quantileIndex] || 0;
        
        return { times, quantile90 };
    }, [processedData, selectedTimeMetric]);

    // Define histogram bars plot
    const histogramBars = vl.markBar({
        filled: true,
        stroke: false
    })
        .data(processedData)
        .transform(
            // Calculate selected time metric and convert to minutes
            {
                calculate: selectedMetric.value === 'response' 
                    ? '(datum.reaction_time + datum.driving_to_scene_time) / 60000'
                    : `datum.${selectedMetric.field} / 60000`,
                as: 'selected_time_minutes'
            },
            // Apply different filters based on metric type
            selectedMetric.value === 'reporting' 
                ? vl.filter('datum.selected_time_minutes > 0 && datum.selected_time_minutes <= 10000') // Include all reporting times
                : vl.filter('datum.selected_time_minutes > 0 && datum.selected_time_minutes <= 200') // Apply filter for other metrics
        )
        .encode(
            vl.x().fieldQ('selected_time_minutes')
                .title(`${selectedMetric.label} (minutes)`)
                .bin({ step: 2 }),
            vl.y().aggregate('count')
                .title('Number of Reports'),
            vl.tooltip().aggregate('count')
                .title('Reports in Bin')
        );

    // Layer the plots together
    const responseTimeHistogram = histogramBars
        .width(width)
        .height(height)
        .title(`Distribution of ${selectedMetric.label}`);

    useEffect(() => {
        const vegaLiteSpec = responseTimeHistogram
            .config(config)
            .toSpec();
        setVegaSpec(vegaLite.compile(vegaLiteSpec).spec);
    }, [processedData, selectedTimeMetric]);

    useEffect(() => {
        if (vegaSpec) {
            const runtime = vega.parse(vegaSpec);
            const view = new vega.View(runtime)
                .renderer("svg")
                .initialize(containerRef.current)
                .hover()

            view.run();

            return () => {
                view.finalize();
            };
        }
    }, [vegaSpec]);

    return (
        <div>
            {/* Time Metric Selection */}
            <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                <h3 style={{ marginBottom: '10px', color: '#333' }}>Select Time Metric</h3>
                <h4 style={{ color: '#333' }}>Filtering out reports with 0 time and those exceeding 200 minutes (other than for Reporting Time)</h4>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    {timeMetrics.map((metric) => (
                        <button
                            key={metric.value}
                            onClick={() => setSelectedTimeMetric(metric.value)}
                            style={{
                                padding: '8px 16px',
                                border: 'none',
                                borderRadius: '4px',
                                backgroundColor: selectedTimeMetric === metric.value ? '#007bff' : '#e9ecef',
                                color: selectedTimeMetric === metric.value ? 'white' : '#495057',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: selectedTimeMetric === metric.value ? 'bold' : 'normal'
                            }}
                        >
                            {metric.label}
                        </button>
                    ))}
                </div>
            </div>

            <div ref={containerRef} />
            
            {/* Summary Statistics */}
            {processedData.length > 0 && (
                <div style={{ marginTop: '20px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                    <h3 style={{ marginBottom: '15px', color: '#333' }}>{selectedMetric.label} Summary</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
                        <div style={{ textAlign: 'center', padding: '15px', backgroundColor: 'white', borderRadius: '4px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#007bff' }}>
                                {processedData.length}
                            </div>
                            <div style={{ fontSize: '14px', color: '#666' }}>Total Reports</div>
                        </div>
                        <div style={{ textAlign: 'center', padding: '15px', backgroundColor: 'white', borderRadius: '4px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#28a745' }}>
                                {Math.round(filteredTimes.times.reduce((sum, time) => sum + time, 0) / filteredTimes.times.length * 10) / 10}
                            </div>
                            <div style={{ fontSize: '14px', color: '#666' }}>Average {selectedMetric.label} (min)</div>
                        </div>
                        <div style={{ textAlign: 'center', padding: '15px', backgroundColor: 'white', borderRadius: '4px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#dc3545' }}>
                                {Math.round(Math.min(...filteredTimes.times) * 10000) / 10000}
                            </div>
                            <div style={{ fontSize: '14px', color: '#666' }}>Fastest {selectedMetric.label} (min)</div>
                        </div>
                        <div style={{ textAlign: 'center', padding: '15px', backgroundColor: 'white', borderRadius: '4px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ffc107' }}>
                                {Math.round(Math.max(...filteredTimes.times) * 10) / 10}
                            </div>
                            <div style={{ fontSize: '14px', color: '#666' }}>Slowest {selectedMetric.label} (min)</div>
                        </div>
                        <div style={{ textAlign: 'center', padding: '15px', backgroundColor: 'white', borderRadius: '4px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#e74c3c' }}>
                                {Math.round(filteredTimes.quantile90 * 10) / 10}
                            </div>
                            <div style={{ fontSize: '14px', color: '#666' }}>90th Percentile (min)</div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GraphApiTimesReports; 